'use client'; // this means that its the same as pages/ components
import { PageHeader } from '@c/ProductsPage';
import MXProductCard from '@c/cards/MXProductCard';
import CheckboxFilterSection from '@c/filters/components/CheckboxFilterSection';
import LocationFilterSection from '@c/filters/components/LocationFilterSection';
import PriceRangeFilterSection from '@c/filters/components/PriceRangeFilterSection';
import RadioFilterSection from '@c/filters/components/RadioFilterSection';
import SaleToggleFilterSection from '@c/filters/components/SaleToggleFilterSection';
import { GridIcon, ListIcon } from '@c/icons';
import BaseModal from '@c/modals/BaseModal';
import { PageMainContent } from '@c/products/PageMainContent';
import { CategoryDocument } from '@models/category';
import ButtonToggle from '@ui/ButtonToggle';
import Carousel from '@ui/Carousel';
import PageSectionHeader from '@ui/PageSectionHeader';
import SafeImage from '@ui/SafeImage';
import Select from '@ui/Select';
import { CONDITIONS } from '@util/createListingHelpers';
import useBikes from '@util/hooks/useBikes';
import { PageSideFilters, TopFilters } from '@util/hooks/useProductsPage';
import { Pagination } from '@util/types/paginator';
import { slugify } from '@util/urlHelpers';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { Button } from '..';
import {
  ProductSortOption,
  ProductDocument,
  BIKE_TYPES,
  ENGINE_SIZES,
  Condition,
} from 'models/product';
import { OrderItemDocument } from 'models/order';
import { Make } from 'models/shared';
import Link from 'next/link';

export const sortingOptions: {
  label: string;
  value: ProductSortOption;
  id: string;
}[] = [
  {
    label: 'Newest Listings',
    value: 'newest',
    id: 'newest',
  },
  {
    label: 'Price: Low to High',
    value: 'priceAsc',
    id: 'priceAsc',
  },
  {
    label: 'Price: High to Low',
    value: 'priceDesc',
    id: 'priceDesc',
  },
];

export interface DirtBikesForSaleProps {
  initialProducts: ProductDocument[];
  initialPageInfo: Pagination;
  initialTopFilters: TopFilters;
  initialSideFilters: PageSideFilters;
  categories: CategoryDocument[];
  featuredBikes: ProductDocument[];
  manufacturerInfo?: any;
  popularCities: string[];
  allCities: string[];
  dirtBikeCats: CategoryDocument[];
  ridingGearCats: CategoryDocument[];
}

const DirtBikesForSalePage = ({
  initialProducts,
  initialPageInfo,
  initialTopFilters,
  initialSideFilters,
  featuredBikes,
  manufacturerInfo: initialManufactuerInfo,
  popularCities,
  allCities,
  dirtBikeCats,
  ridingGearCats,
}: DirtBikesForSaleProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Filters
  const [topFilters, setTopFilters] = useState<TopFilters>(initialTopFilters);
  const [sideFilters, setSideFilters] =
    useState<PageSideFilters>(initialSideFilters);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { bikeYears, bikeMakes, bikeModels } = useBikes(
    sideFilters.bikeYears,
    sideFilters.bikeMakes as Make[],
    ['Dirt Bikes']
  );

  const handleSideFiltersChange = (newSideFilters: PageSideFilters) => {
    setSideFilters(newSideFilters);
    updateUrl(newSideFilters, topFilters);
  };

  const updateUrl = (
    newSideFilters: PageSideFilters,
    newTopFilters: TopFilters
  ) => {
    let url = '';
    const urlAdditions = [];
    const urlSuffixes = [];
    if (
      newSideFilters.bikeConditions?.length &&
      newSideFilters.bikeConditions?.length === 1
    ) {
      urlAdditions.push(
        slugify(newSideFilters.bikeConditions[0].toLowerCase())
      );
    }
    if (newSideFilters.bikeTypes?.length) {
      newSideFilters.bikeTypes.forEach((type) => {
        urlAdditions.push(slugify(type.toLowerCase()));
      });
    }
    if (newSideFilters.bikeYears?.length) {
      newSideFilters.bikeYears.forEach((year) => {
        urlAdditions.push(year);
      });
    }
    if (newSideFilters.bikeMakes?.length) {
      newSideFilters.bikeMakes.forEach((make) => {
        urlAdditions.push(slugify(make.toLowerCase()));
      });
    }
    if (newSideFilters.bikeModels?.length) {
      newSideFilters.bikeModels.forEach((model) => {
        urlAdditions.push(slugify(model.toLowerCase()));
      });
    }
    if (newSideFilters.bikeEngineSizes?.length) {
      newSideFilters.bikeEngineSizes.forEach((engineSize) => {
        urlAdditions.push(engineSize);
      });
    }
    if (newSideFilters.onsale) {
      urlAdditions.push('on-sale');
    }
    if (newSideFilters.location) {
      urlSuffixes.push(
        '-near-' + slugify(newSideFilters.location.address.toLowerCase())
      );
    }
    urlAdditions.push('dirt-bikes-for-sale');
    url += urlAdditions.join('-');
    url += urlSuffixes.join('-');

    if (
      (newSideFilters.bikeTypes ?? []).length > 1 ||
      (newSideFilters.bikeYears ?? []).length > 1 ||
      (newSideFilters.bikeMakes ?? []).length > 1 ||
      (newSideFilters.bikeModels ?? []).length > 1 ||
      (newSideFilters.bikeEngineSizes ?? []).length > 1 ||
      (newSideFilters.shipFrom ?? []).length > 1 ||
      (newSideFilters.shipTo ?? []).length > 1
    ) {
      url = '/filters/' + url;
    }
    if (!url.startsWith('/')) {
      url = '/' + url;
    }
    let query = searchParams;
    let queryParams = [];
    if (query) {
      if (query.get('page')) {
        queryParams.push(`page=${query.get('page')}`);
      }
    }
    if (newSideFilters.maxPrice) {
      queryParams.push(`maxPrice=${newSideFilters.maxPrice}`);
    }
    if (newSideFilters.minPrice) {
      queryParams.push(`minPrice=${newSideFilters.minPrice}`);
    }
    if (newTopFilters.sort && newTopFilters.sort !== 'newest') {
      queryParams.push(`sort=${newTopFilters.sort}`);
    }
    if (newSideFilters.radius) {
      queryParams.push(`radius=${newSideFilters.radius}`);
    }
    if (queryParams.length) {
      if (url.endsWith('/')) {
        url = url.slice(0, -1);
      }
      url += `?${queryParams.join('&')}`;
    }
    router.replace(url, {
      scroll: false,
    });
  };

  const handleTopFiltersChange = (newTopFilters: TopFilters) => {
    setTopFilters(newTopFilters);
    updateUrl(sideFilters, newTopFilters);
  };

  const getTitle = () => {
    const titleAdditions: string[] = [];
    const titleSuffixes: string[] = [];
    if (sideFilters.location?.address) {
      return 'Dirt Bikes for Sale Near ' + sideFilters.location.address;
    }
    if (sideFilters.bikeConditions?.length === 1) {
      titleAdditions.push(sideFilters.bikeConditions[0]);
    }
    if (sideFilters.bikeYears?.length === 1) {
      titleAdditions.push(sideFilters.bikeYears[0]);
    }
    if (sideFilters.bikeMakes?.length === 1) {
      titleAdditions.push(sideFilters.bikeMakes[0]);
    }
    if (sideFilters.bikeTypes?.length === 1) {
      titleAdditions.push(sideFilters.bikeTypes[0]);
    }
    if (sideFilters.bikeModels?.length === 1) {
      titleAdditions.push(sideFilters.bikeModels[0]);
    }
    if (sideFilters.bikeEngineSizes?.length === 1) {
      titleAdditions.push(sideFilters.bikeEngineSizes[0]);
    }
    return (
      titleAdditions.join(' ') +
      ' Dirt Bikes for Sale ' +
      titleSuffixes.join(' ')
    );
  };

  const getHeaderImage = () => {
    return initialManufactuerInfo
      ? initialManufactuerInfo.header_image
      : '/dirt-bike-banner.png';
  };

  return (
    <div className="w-full">
      <div className=" mx-auto max-w-default">
        <PageHeader
          headerBreadcrumb={{
            label: `Dirt Bikes`,
            href: '/dirt-bikes-for-sale',
          }}
          pageHeaderImage={getHeaderImage()}
          pageHeaderTitle="Dirt Bikes"
          pageHeaderGradient="white"
          pageHeaderBaseBackground="white"
        />

        <PageMainContent
          view={topFilters.view}
          clearAllFilters={() => {
            handleSideFiltersChange({
              bikeConditions: [],
              bikeTypes: [],
              bikeYears: [],
              bikeMakes: [],
              bikeModels: [],
              bikeEngineSizes: [],
              location: null,
              minPrice: 0,
              maxPrice: 0,
              conditions: [],
              brands: [],
              numberSizes: [],
              letterSizes: [],
              onsale: false,
            });
          }}
          isFetching={false}
          products={initialProducts}
          pageInfo={initialPageInfo}
          filterState={sideFilters}
          onFiltersChange={handleSideFiltersChange}
          attribution={{
            // This doesn't matter for now, but change when we add checking out dirt bikes on site
            data_source: 'dirt-bikes',
            page: pathname ?? '',
            section: `category-page-${topFilters.view}`,
          }}
          filters={
            <div className="flex w-full min-w-[28.2rem] flex-col rounded-brand bg-white p-4 sm:max-w-[28.2rem]">
              <LocationFilterSection
                radius={sideFilters.radius ?? 25}
                setRadius={(radius) => {
                  handleSideFiltersChange({
                    ...sideFilters,
                    radius,
                  });
                }}
                location={sideFilters.location}
                popularCities={popularCities}
                allCities={allCities}
                setLocation={(location) => {
                  handleSideFiltersChange({
                    ...sideFilters,
                    location,
                  });
                }}
              />
              <CheckboxFilterSection
                title="Bike Type"
                filterOptions={BIKE_TYPES.map((type) => ({
                  key: type,
                  label: type,
                  id: type,
                }))}
                onChange={(filters) => {
                  handleSideFiltersChange({
                    ...sideFilters,
                    bikeTypes: filters,
                  });
                }}
                filterState={sideFilters.bikeTypes ?? []}
              />
              <CheckboxFilterSection
                title="Year"
                filterOptions={bikeYears.map((year) => ({
                  key: `${year}`,
                  label: `${year}`,
                  id: `${year}`,
                }))}
                onChange={(filters) => {
                  handleSideFiltersChange({
                    ...sideFilters,
                    bikeYears: filters,
                  });
                }}
                filterState={sideFilters.bikeYears ?? []}
              />
              <CheckboxFilterSection
                title="Make"
                filterOptions={bikeMakes.map((make) => ({
                  key: make,
                  label: make,
                  id: make,
                }))}
                onChange={(filters) => {
                  handleSideFiltersChange({
                    ...sideFilters,
                    bikeMakes: filters,
                  });
                }}
                filterState={sideFilters.bikeMakes ?? []}
              />
              <CheckboxFilterSection
                title="Model"
                filterOptions={bikeModels.map((bike) => ({
                  key: bike,
                  label: bike,
                  id: bike,
                }))}
                noFiltersMessage="Select a make and year"
                onChange={(filters) => {
                  handleSideFiltersChange({
                    ...sideFilters,
                    bikeModels: filters,
                  });
                }}
                filterState={sideFilters.bikeModels ?? []}
              />
              <CheckboxFilterSection
                title="Engine Size"
                filterOptions={ENGINE_SIZES.map((size) => ({
                  key: size + 'cc',
                  label: size + 'cc',
                  id: size + 'cc',
                }))}
                onChange={(filters) => {
                  handleSideFiltersChange({
                    ...sideFilters,
                    bikeEngineSizes: filters,
                  });
                }}
                filterState={sideFilters.bikeEngineSizes ?? []}
              />
              <PriceRangeFilterSection
                title="Price"
                onChange={(min, max) => {
                  handleSideFiltersChange({
                    ...sideFilters,
                    minPrice: min,
                    maxPrice: max,
                  });
                }}
                min={sideFilters.minPrice}
                max={sideFilters.maxPrice}
              />
              <RadioFilterSection
                title="Conditions"
                filterOptions={CONDITIONS.map((c) => ({
                  key: c.replaceAll(' ', '-'),
                  label: c,
                }))}
                onChange={(filters) => {
                  handleSideFiltersChange({
                    ...sideFilters,
                    bikeConditions: filters as Condition[],
                  });
                }}
                initialFilters={sideFilters.bikeConditions as string[]}
              />
              <SaleToggleFilterSection
                enabled={sideFilters.onsale ?? false}
                onChange={(enabled) => {
                  handleSideFiltersChange({
                    ...sideFilters,
                    onsale: enabled,
                  });
                }}
              />
            </div>
          }
          controls={
            <div className="flex w-full flex-col gap-[1.6rem] lg:flex-row">
              <div className="flex items-center gap-[1.6rem] whitespace-nowrap">
                <span>Sort by</span>
                <Select
                  options={sortingOptions}
                  allowEmpty={false}
                  defaultValue={topFilters.sort}
                  value={topFilters.sort}
                  onChange={({ value }) => {
                    handleTopFiltersChange({
                      ...topFilters,
                      sort: value as ProductSortOption,
                    });
                  }}
                />
              </div>
              <div className="flex w-full flex-col justify-between gap-[1.6rem] sm:flex-row">
                <ButtonToggle
                  buttons={[
                    {
                      icon: <GridIcon />,
                      key: 'grid',
                      onClick: () => {
                        handleTopFiltersChange({
                          ...topFilters,
                          view: 'grid',
                        });
                      },
                      selected: topFilters.view === 'grid',
                    },
                    {
                      icon: <ListIcon />,
                      key: 'list',
                      onClick: () => {
                        handleTopFiltersChange({
                          ...topFilters,
                          view: 'list',
                        });
                      },
                      selected: topFilters.view === 'list',
                    },
                  ]}
                />
              </div>
              <Button onClick={() => setDrawerOpen(true)} text="Filter" />
            </div>
          }
        />
        <BaseModal
          isOpen={drawerOpen}
          dismiss={() => setDrawerOpen(false)}
          hideCloseIcon
        >
          <div className="overflow-y-scroll p-8">
            <Button
              width="fluid"
              type="secondary"
              onClick={() => setDrawerOpen(false)}
              text="Apply"
            />

            <LocationFilterSection
              radius={sideFilters.radius ?? 25}
              setRadius={(radius) => {
                handleSideFiltersChange({
                  ...sideFilters,
                  radius,
                });
              }}
              location={sideFilters.location}
              popularCities={popularCities}
              allCities={allCities}
              setLocation={(location) => {
                handleSideFiltersChange({
                  ...sideFilters,
                  location,
                });
              }}
            />

            <CheckboxFilterSection
              title="Bike Type"
              filterOptions={BIKE_TYPES.map((type) => ({
                key: type,
                label: type,
                id: type,
              }))}
              onChange={(filters) => {
                handleSideFiltersChange({
                  ...sideFilters,
                  bikeTypes: filters,
                });
              }}
              filterState={sideFilters.bikeTypes ?? []}
            />

            <CheckboxFilterSection
              title="Year"
              filterOptions={bikeYears.map((year) => ({
                key: `${year}`,
                label: `${year}`,
                id: `${year}`,
              }))}
              onChange={(filters) => {
                handleSideFiltersChange({
                  ...sideFilters,
                  bikeYears: filters,
                });
              }}
              filterState={sideFilters.bikeYears ?? []}
            />

            <CheckboxFilterSection
              title="Make"
              filterOptions={bikeMakes.map((make) => ({
                key: make,
                label: make,
                id: make,
              }))}
              onChange={(filters) => {
                handleSideFiltersChange({
                  ...sideFilters,
                  bikeMakes: filters,
                });
              }}
              filterState={sideFilters.bikeMakes ?? []}
            />

            <CheckboxFilterSection
              title="Model"
              filterOptions={bikeModels.map((bike) => ({
                key: bike,
                label: bike,
                id: bike,
              }))}
              noFiltersMessage="Select a make and year"
              onChange={(filters) => {
                handleSideFiltersChange({
                  ...sideFilters,
                  bikeModels: filters,
                });
              }}
              filterState={sideFilters.bikeModels ?? []}
            />

            <CheckboxFilterSection
              title="Engine Size"
              filterOptions={ENGINE_SIZES.map((size) => ({
                key: size + 'cc',
                label: size + 'cc',
                id: size + 'cc',
              }))}
              onChange={(filters) => {
                handleSideFiltersChange({
                  ...sideFilters,
                  bikeEngineSizes: filters,
                });
              }}
              filterState={sideFilters.bikeEngineSizes ?? []}
            />

            <PriceRangeFilterSection
              title="Price"
              onChange={(min, max) => {
                handleSideFiltersChange({
                  ...sideFilters,
                  minPrice: min,
                  maxPrice: max,
                });
              }}
              min={sideFilters.minPrice}
              max={sideFilters.maxPrice}
            />

            <RadioFilterSection
              title="Conditions"
              filterOptions={CONDITIONS.map((c) => ({
                key: c.replaceAll(' ', '-'),
                label: c,
              }))}
              onChange={(filters) => {
                handleSideFiltersChange({
                  ...sideFilters,
                  bikeConditions: filters as Condition[],
                });
              }}
              initialFilters={sideFilters.bikeConditions as string[]}
            />

            <SaleToggleFilterSection
              enabled={sideFilters.onsale ?? false}
              onChange={(enabled) => {
                handleSideFiltersChange({
                  ...sideFilters,
                  onsale: enabled,
                });
              }}
            />
            <div className="p-4">
              <Button
                width="fluid"
                type="secondary"
                onClick={() => setDrawerOpen(false)}
                text="Apply"
              />
            </div>
          </div>
        </BaseModal>
      </div>

      {!!initialManufactuerInfo && (
        <div className="hidden w-full gap-[1.2rem]  bg-white px-[1.6rem] sm:block sm:pl-default-padding-x">
          <div className="mx-auto flex max-w-default gap-x-[1.5rem] px-5 py-7">
            <SafeImage
              className="max-h-[64px] object-contain"
              src={initialManufactuerInfo.image}
              alt={initialManufactuerInfo.name}
              key={initialManufactuerInfo.image}
              width={64}
              height={64}
            />
            <div>
              <h2 className="mb-2 text-left text-2xl font-semibold">
                {initialManufactuerInfo.name}
              </h2>
              {initialManufactuerInfo.description && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: initialManufactuerInfo.description,
                  }}
                ></div>
              )}
              {initialManufactuerInfo.second_description && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: initialManufactuerInfo.second_description,
                  }}
                ></div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="w-full bg-[#F4F4F4] p-8">
        <div className="flex flex-col items-start justify-between gap-4 px-8 py-4 sm:mx-auto sm:max-w-content sm:px-0 ">
          <span className="text-[1.8rem] font-semibold">
            Shop Dirt Bikes By Manufacturer
          </span>
          <div className="grid w-full grid-cols-2 gap-y-[1.6rem]  sm:grid-cols-3 ">
            {[
              'Beta',
              'Cobra',
              'Gas Gas',
              'Honda',
              'Husqvarna',
              'KTM',
              'Kawasaki',
              'Suzuki',
              'Yamaha',
            ].map((make) => (
              <Link
                className="underline"
                key={make}
                href={`/shop/${slugify(make)}-dirt-bikes-for-sale`}
              >
                {make} Dirt Bikes For Sale
              </Link>
            ))}
          </div>
        </div>
      </div>
      {!!dirtBikeCats.length && (
        <div className="w-full bg-[#F4F4F4] p-8">
          <div className="flex flex-col items-start justify-between gap-4 px-8 py-4 sm:mx-auto sm:max-w-content sm:px-0">
            <Link
              href={`/shop/dirt-bike-parts`}
              className="text-[1.8rem] font-semibold"
            >
              Shop Dirt Bike Parts
            </Link>
            <div className="grid w-full grid-cols-2 gap-y-[1.6rem]  sm:grid-cols-3 ">
              {dirtBikeCats.map((category) => (
                <Link
                  className="underline"
                  key={category.full_slug}
                  href={category.full_slug}
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
      {!!ridingGearCats.length && (
        <div className="w-full bg-[#F4F4F4] p-8">
          <div className="flex flex-col items-start justify-between gap-4 px-8 py-4 sm:mx-auto sm:max-w-content sm:px-0">
            <Link
              href={'/shop/dirt-bike-riding-gear'}
              className="text-[1.8rem] font-semibold"
            >
              Shop Dirt Bike Riding Gear
            </Link>
            <div className="grid w-full grid-cols-2 gap-y-[1.6rem]  sm:grid-cols-3 ">
              {ridingGearCats.map((category) => (
                <Link
                  className="underline"
                  key={category.full_slug}
                  href={category.full_slug}
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// function MapIcon() {
//   return (
//     <svg
//       width="20"
//       height="20"
//       viewBox="0 0 20 20"
//       fill="none"
//       xmlns="http://www.w3.org/2000/svg"
//     >
//       <path
//         d="M7.5 14.375V3.125"
//         stroke="currentColor"
//         strokeWidth="1.5"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//       <path
//         d="M12.5 5.625V16.875"
//         stroke="currentColor"
//         strokeWidth="1.5"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//       <path
//         d="M7.5 14.375L2.5 15.625V4.375L7.5 3.125L12.5 5.625L17.5 4.375V15.625L12.5 16.875L7.5 14.375Z"
//         stroke="currentColor"
//         strokeWidth="1.5"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//     </svg>
//   );
// }
const renderProductCards = (
  productItems: ProductDocument[],
  attribution: OrderItemDocument['attribution']
) =>
  productItems.map((product, i) => (
    <MXProductCard
      product={product}
      key={product.sku + '-' + i}
      attribution={attribution}
    />
  ));

export default DirtBikesForSalePage;
